import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import RadarVisual from "@/components/visuals/RadarVisual";
import DataFoundationVisual from "@/components/visuals/DataFoundationVisual";
import RoutingVisual from "@/components/visuals/RoutingVisual";
import PipelineOpsVisual from "@/components/visuals/PipelineOpsVisual";
import AIBriefingVisual from "@/components/visuals/AIBriefingVisual";
import { SectionHeader } from "@/components/RevenueEngine";

const LAYERS = [
  {
    tag: "Layer 01",
    name: "The Data Foundation",
    desc: "Everything downstream depends on this. We audit what's broken, clean what's dirty, and keep it accurate automatically. You can't build on bad data.",
    inside: "Data Foundation",
    color: "#007AFF",
    Visual: DataFoundationVisual,
  },
  {
    tag: "Layer 02",
    name: "The Intelligence Radar",
    desc: "The system watches your market 24/7. Funding rounds, new hires, website visits, tech installs. You know the moment a buyer shows intent.",
    inside: "TAM & Sales Intelligence → Signal System",
    color: "#00F0FF",
    Visual: RadarVisual,
  },
  {
    tag: "Layer 03",
    name: "The Routing System",
    desc: "When a signal fires, the right rep gets it instantly. Matched by territory, account tier, and capacity. No manual triage, no dropped leads.",
    inside: "ABM Infrastructure → Inbound & PLG",
    color: "#D946EF",
    Visual: RoutingVisual,
  },
  {
    tag: "Layer 04",
    name: "The Operations Engine",
    desc: "Deal stages advance when conditions are met. Follow-ups trigger on their own. Stalled deals surface before they go cold. The pipeline runs itself.",
    inside: "Sales-Ops Automation → Network & Expansion",
    color: "#FFB800",
    Visual: PipelineOpsVisual,
  },
  {
    tag: "Layer 05",
    name: "The AI Enablement",
    desc: "Reps don't research. Before every call, the system generates a full account brief: signals, stakeholders, context, and a suggested first email. Ready to send.",
    inside: "Rep Intelligence & AI Agents",
    color: "#CCFF00",
    Visual: AIBriefingVisual,
  },
];

export default function LayerTimeline() {
  const [active, setActive] = useState(0);
  const { Visual, color } = LAYERS[active];

  return (
    <section
      id="architecture"
      data-testid="layer-timeline"
      className="relative bg-[#050505] border-t border-white/10 py-28 md:py-40"
    >
      <div className="absolute inset-0 precision-grid-dense opacity-20 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <SectionHeader
          overline="The Architecture / 03"
          title={<>How It<br /><span className="text-white">Works.</span></>}
          sub="Every pipeline we build follows the same architecture. Five layers. Each one builds on the last."
        />

        <div className="mt-16 grid grid-cols-12 gap-6 lg:gap-10">
          {/* Left — layer list with progress rail */}
          <div className="col-span-12 lg:col-span-7 relative">
            {/* Left rail */}
            <div className="hidden lg:block absolute left-3 top-6 bottom-6 w-px bg-white/10" />
            <div
              className="hidden lg:block absolute left-3 top-6 w-px transition-all duration-500"
              style={{
                height: `${((active + 1) / LAYERS.length) * 90}%`,
                background: `linear-gradient(to bottom, transparent, ${color})`,
                boxShadow: `0 0 14px ${color}`,
              }}
            />

            <div className="space-y-3">
              {LAYERS.map((layer, i) => {
                const isActive = active === i;
                return (
                  <button
                    key={layer.name}
                    onClick={() => setActive(i)}
                    onMouseEnter={() => setActive(i)}
                    data-testid={`layer-${i}`}
                    className={`group w-full text-left relative border transition-all duration-300 pl-8 lg:pl-12 ${
                      isActive
                        ? "border-white/25 bg-white/[0.03]"
                        : "border-white/10 hover:border-white/20"
                    }`}
                  >
                    {/* Rail node */}
                    <span
                      className="hidden lg:block absolute left-[7px] top-7 h-2 w-2 rounded-full transition-all"
                      style={{
                        background: isActive ? layer.color : "rgba(255,255,255,0.12)",
                        boxShadow: isActive ? `0 0 12px ${layer.color}` : "none",
                        transform: "translateX(-50%)",
                      }}
                    />

                    <div className="p-5 md:p-6">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <span
                          className="font-mono text-[10px] uppercase tracking-[0.3em]"
                          style={{ color: isActive ? layer.color : "#52525B" }}
                        >
                          {layer.tag}
                        </span>
                        <span className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider">
                          {layer.inside}
                        </span>
                      </div>
                      <h3
                        className={`mt-2 font-display font-bold uppercase tracking-tighter text-2xl md:text-3xl transition-colors ${
                          isActive ? "text-white" : "text-zinc-500 group-hover:text-zinc-200"
                        }`}
                      >
                        {layer.name}
                      </h3>
                      {isActive && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-3 font-body text-zinc-400 text-[14px] leading-relaxed max-w-[56ch]"
                        >
                          {layer.desc}
                        </motion.p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right — sticky visual */}
          <div className="col-span-12 lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div
                className="relative border p-6 md:p-8 min-h-[420px] lg:min-h-[520px] flex items-center justify-center overflow-hidden"
                style={{
                  background: "#0a0a0a",
                  borderColor: color + "33",
                  boxShadow: `inset 0 0 0 1px ${color}11`,
                }}
                data-testid="layer-visual-panel"
              >
                {/* Corner brackets */}
                <Corners color={color} />

                {/* Layer marker */}
                <div className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color }}>
                  {LAYERS[active].tag}
                </div>
                <div className="absolute top-4 right-4 flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color }}>
                    Active
                  </span>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className="w-full h-full flex items-center justify-center px-2 pt-6 pb-2"
                  >
                    <Visual />
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                <span>layer {active + 1} of {LAYERS.length}</span>
                <span className="flex items-center gap-1.5">
                  {LAYERS.map((_, i) => (
                    <span
                      key={i}
                      className="h-1 w-6 transition-colors"
                      style={{
                        background: i === active ? color : "#1f1f1f",
                        boxShadow: i === active ? `0 0 6px ${color}` : "none",
                      }}
                    />
                  ))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Corners({ color }) {
  const s = { borderColor: color + "88" };
  return (
    <>
      <span className="absolute top-0 left-0 w-3 h-3 border-l border-t" style={s} />
      <span className="absolute top-0 right-0 w-3 h-3 border-r border-t" style={s} />
      <span className="absolute bottom-0 left-0 w-3 h-3 border-l border-b" style={s} />
      <span className="absolute bottom-0 right-0 w-3 h-3 border-r border-b" style={s} />
    </>
  );
}
